import {
  Button,
  ButtonColor,
  ButtonSize,
  Combobox,
  Option,
  TextInput,
} from '@code/innovance-react-ui-kit';

import { yupResolver } from '@hookform/resolvers';
import React, { FC, useCallback, useEffect, useState } from 'react';
import { Controller, FieldError, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import {
  ContactInfoChange,
  ContactInfoChangePageValidation,
  CustomerContactInfo,
} from 'src/front/api/apiInstance';
import {
  ContactInfoChangeData,
  ContactInfoChangeResponse,
  PhoneType,
  ReferenceDataType,
  ScreenProcessType,
} from 'src/front/api/generated';

import usePRM from 'src/front/common/hooks/usePRM';

import { dictionaryLineFormat, formatNumeric } from 'src/front/common/utils/formUtils';
import { MobilePhoneSchema } from 'src/front/common/utils/validationSchemas';

import {
  Container,
  Form,
  FormElement,
  FormRow,
  InfoLabel,
  InfoRow,
  LineSeparator,
  Separator,
  Subtitle,
  Title,
  WarningText,
} from 'src/front/components/FormStyles';
import { useCustomerSearchState } from 'src/front/contexts/ContactInfoContext';
import {
  AreaCode,
  FlexFormElement,
  PhoneNumber,
  PhoneValidations,
} from 'src/front/pages/applications/card-entry/phone-email-entry/PhoneEmailEntry';

interface Props {
  goToNextStep: () => void;
  isEdit?: boolean;
  contactCustomerName?: string;
  customerId?: string;
}

interface FormData {
  mobilePhoneCountry: Option | null;
  mobilePhoneFieldCode: string;
  mobilePhoneTelephoneNo: string;
}
const defaultValues = {
  mobilePhoneCountry: null,
  mobilePhoneFieldCode: '',
  mobilePhoneTelephoneNo: '',
};

const ContactInformation: FC<Props> = ({ goToNextStep, contactCustomerName, isEdit }) => {
  const { t } = useTranslation();
  const { customerId, clientName } = useCustomerSearchState();
  const [formValidations, setFormValidations] = useState<PhoneValidations>();
  const countryPhoneCodes = usePRM(ReferenceDataType.COUNTRYCODETELEPHONE);
  const [isContactDataChangedWarning, setIsContactDataChangedWarning] = useState<string>('');
  const { handleSubmit, errors, setValue, control, watch, formState } = useForm<FormData>({
    defaultValues,
    resolver: yupResolver(MobilePhoneSchema, {
      abortEarly: false,
      context: { formValidations },
    }),
  });

  const loadPhoneEmailInformation = useCallback(async () => {
    const { data } = await ContactInfoChangePageValidation.getContactInfoChangePageData();

    let mobilePhone: ContactInfoChangeData | ContactInfoChangeResponse | undefined;
    if (data.result === 'OK' && data.pageData) {
      mobilePhone = data.pageData;
    } else {
      const { data: response } = await ContactInfoChange.getPhoneNumberForContactInfoChange({
        customerId: customerId || '',
      });
      mobilePhone = response;
    }

    const countryCode = countryPhoneCodes.find(
      (country) =>
        country.value ===
        (typeof mobilePhone?.countryCode === 'string'
          ? mobilePhone?.countryCode
          : mobilePhone?.countryCode?.code)
    );

    [
      ...(mobilePhone
        ? [
            {
              name: 'mobilePhoneCountry',
              value: countryCode,
            },
            {
              name: 'mobilePhoneFieldCode',
              value: mobilePhone.fieldCode,
            },
            {
              name: 'mobilePhoneTelephoneNo',
              value: mobilePhone.telephoneNo,
            },
          ]
        : []),
    ].forEach(({ name, value }) => setValue(name, value as {} | undefined));
    countryCode && handleCountryChange('mobilePhone', PhoneType.MOBILEPHONE)(countryCode);
  }, [countryPhoneCodes, customerId, setValue]);

  useEffect(() => {
    if (countryPhoneCodes.length) {
      loadPhoneEmailInformation();
    }
  }, [countryPhoneCodes.length, loadPhoneEmailInformation]);

  const handleCountryChange = (
    fieldName: string,
    phoneType: PhoneType,
    onChange?: (option: Option | null) => void
  ) => async (country: Option | null) => {
    onChange && onChange(country);
    if (country) {
      const { data } = await CustomerContactInfo.getCustomerContactInfoFormat({
        phoneType,
        countryCode: country?.value || '',
      });
      setFormValidations((state) => ({ ...state, [fieldName]: data }));
    }
  };

  const onSubmit = async (form: FormData): Promise<void> => {
    const { data } = await ContactInfoChangePageValidation.validateContactInfoChangePageData({
      countryCode: dictionaryLineFormat(form.mobilePhoneCountry),
      fieldCode: form.mobilePhoneFieldCode,
      telephoneNo: form.mobilePhoneTelephoneNo,
      screenProcessType: isEdit ? ScreenProcessType.EDIT : ScreenProcessType.NEW,
    });

    if (data.result === 'OK') {
      isEdit && goToNextStep();
      data.isMobileDataChanged
        ? goToNextStep()
        : setIsContactDataChangedWarning('general_thereIsNoChange_errorMessage');
    }
  };

  return (
    <Container>
      <Title>
        <Trans i18nKey="changeContactInfo_title" />
      </Title>
      <Separator />
      <InfoRow>
        <InfoLabel>
          <Trans i18nKey="changeFundClientInfo_clientName_text" />:
        </InfoLabel>
        {contactCustomerName || clientName?.clientName}
      </InfoRow>
      <FormRow>
        <LineSeparator />
      </FormRow>
      <Separator />
      <Subtitle>
        <Trans i18nKey="changeFundClientInfo_documentInfo_subtitle" />
      </Subtitle>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <FormRow>
          <FormElement>
            <Controller
              control={control}
              name="mobilePhoneCountry"
              render={({ onChange, value }) => (
                <Combobox
                  errorMessage={t((errors?.mobilePhoneCountry as FieldError)?.message as string)}
                  items={countryPhoneCodes}
                  label={t('cardEntry_phoneEmailEntry_countryPhoneCode_label')}
                  name="mobilePhoneCountry"
                  onChange={handleCountryChange(
                    'mobilePhoneCountry',
                    PhoneType.CONTACTMOBILEPHONE,
                    onChange
                  )}
                  searchPlaceholder={t('cardEntry_phoneEmailEntry_searchCountryCode_text')}
                  value={value}
                  disabled
                  required
                  searchable
                />
              )}
            />
          </FormElement>
          <FlexFormElement>
            <AreaCode>
              <Controller
                control={control}
                name="mobilePhoneFieldCode"
                render={({ onChange, value }) => (
                  <TextInput
                    disabled={!watch('mobilePhoneCountry')}
                    errorMessage={t(errors.mobilePhoneFieldCode?.message as string, {
                      min: formValidations?.mobilePhone?.minFieldCodeLength,
                    })}
                    formatter={formatNumeric}
                    label={t('cardEntry_phoneEmailEntry_areaCode_label')}
                    maxLength={formValidations?.mobilePhone?.maxFieldCodeLength}
                    name="mobilePhoneFieldCode"
                    onChange={onChange}
                    value={value}
                    required
                  />
                )}
              />
            </AreaCode>
            <PhoneNumber>
              <Controller
                control={control}
                name="mobilePhoneTelephoneNo"
                render={({ onChange, value }) => (
                  <TextInput
                    disabled={!watch('mobilePhoneCountry')}
                    errorMessage={t(errors.mobilePhoneTelephoneNo?.message as string, {
                      min: formValidations?.mobilePhone?.minTelephoneNoLength,
                    })}
                    formatter={formatNumeric}
                    label={t('cardEntry_phoneEmailEntry_phoneNumber_label')}
                    maxLength={formValidations?.mobilePhone?.maxTelephoneNoLength}
                    name="mobilePhoneTelephoneNo"
                    onChange={onChange}
                    value={value}
                    required
                  />
                )}
              />
            </PhoneNumber>
          </FlexFormElement>
        </FormRow>
        <Separator />
        {isContactDataChangedWarning ? (
          <WarningText>
            <Trans i18nKey={isContactDataChangedWarning} />
          </WarningText>
        ) : null}
        <FormRow>
          <Button
            color={ButtonColor.Primary}
            disabled={formState.isSubmitting}
            size={ButtonSize.Medium}
            text={t('general_continue_button')}
            type="submit"
          />
        </FormRow>
      </Form>
    </Container>
  );
};

export default ContactInformation;
